import { useState } from 'react';
import Input from '../ui/Input';
import FieldEditor, { emptyField, newId } from './FieldEditor';

/**
 * The Fields tab of the workflow "Edit step" modal. Top-level fields can be grouped into named
 * sections by dragging them in (via the ⠿ handle) — a section lets staff save just its own
 * fields and move on to the next step early (WorkflowEngine::processSaveSection()/'next').
 * Fields left ungrouped stay part of the step's single overall Complete action, same as before
 * this feature existed.
 *
 * `documents` + `onDocumentsChange` (the step's required_documents array/setter) are passed
 * straight through to each <FieldEditor /> so an options field can also reveal a required
 * document per answer, not just a field — see FieldEditor's docblock.
 */

function DraggableRow({ field, fields, onFieldsChange, documents, onDocumentsChange, docTypeOptions, onDragStart, dragging }) {
  return (
    <div className={`flex items-start gap-1.5 ${dragging ? 'opacity-40' : ''}`}>
      <span
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', field.id);
          onDragStart(field.id);
        }}
        onDragEnd={() => onDragStart(null)}
        title="Drag into a section"
        className="mt-3 shrink-0 cursor-grab select-none text-sm text-gray-300 hover:text-gray-500"
      >
        ⠿
      </span>
      <div className="flex-1">
        <FieldEditor
          field={field}
          fields={fields}
          onFieldsChange={onFieldsChange}
          documents={documents}
          onDocumentsChange={onDocumentsChange}
          docTypeOptions={docTypeOptions}
        />
      </div>
    </div>
  );
}

function DropZone({ sectionId, onDropField, className, children }) {
  const [over, setOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const fieldId = e.dataTransfer.getData('text/plain');
        if (fieldId) onDropField(fieldId, sectionId);
      }}
      className={`${className} ${over ? 'ring-2 ring-primary' : ''}`}
    >
      {children}
    </div>
  );
}

export default function FieldSectionsEditor({
  fields,
  sections,
  onFieldsChange,
  onSectionsChange,
  documents = null,
  onDocumentsChange = null,
  docTypeOptions = [],
}) {
  const [draggingId, setDraggingId] = useState(null);

  const topLevel = fields.filter((f) => !f.depends_on);
  const ungrouped = topLevel.filter((f) => !f.section_id);
  const inSection = (sectionId) => topLevel.filter((f) => f.section_id === sectionId);

  const moveField = (fieldId, sectionId) => {
    onFieldsChange(fields.map((f) => (f.id === fieldId ? { ...f, section_id: sectionId } : f)));
    // A successful drop moves the field to a different list, unmounting its drag-handle node
    // before the browser's own dragend can fire reliably — clear the dragging flag here instead,
    // otherwise the field re-mounts in its new section stuck at the dimmed opacity-40 drag style.
    setDraggingId(null);
  };

  const addSection = () => onSectionsChange([...sections, { id: newId(), title: `Section ${sections.length + 1}` }]);
  const renameSection = (id, title) => onSectionsChange(sections.map((s) => (s.id === id ? { ...s, title } : s)));
  const removeSection = (id) => {
    onSectionsChange(sections.filter((s) => s.id !== id));
    onFieldsChange(fields.map((f) => (f.section_id === id ? { ...f, section_id: null } : f)));
  };

  const addField = (sectionId) => onFieldsChange([...fields, { ...emptyField(), section_id: sectionId }]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="block text-sm font-medium text-gray-700">Fields</p>
        <div className="flex gap-3">
          <button type="button" onClick={addSection} className="text-xs text-primary hover:underline">
            + Add section
          </button>
          <button type="button" onClick={() => addField(null)} className="text-xs text-primary hover:underline">
            + Add field
          </button>
        </div>
      </div>

      <DropZone sectionId={null} onDropField={moveField} className="space-y-2 rounded-md border border-dashed border-gray-200 p-2">
        {sections.length > 0 && (
          <p className="text-xs text-gray-400">
            {ungrouped.length > 0 ? 'Ungrouped — drag into a section to let staff save it on its own.' : 'Drag a field here to remove it from its section.'}
          </p>
        )}
        {ungrouped.map((f) => (
          <DraggableRow
            key={f.id}
            field={f}
            fields={fields}
            onFieldsChange={onFieldsChange}
            documents={documents}
            onDocumentsChange={onDocumentsChange}
            docTypeOptions={docTypeOptions}
            onDragStart={setDraggingId}
            dragging={draggingId === f.id}
          />
        ))}
        {fields.length === 0 && <p className="text-xs text-gray-400">No fields yet.</p>}
      </DropZone>

      {sections.map((section) => (
        <DropZone
          key={section.id}
          sectionId={section.id}
          onDropField={moveField}
          className="space-y-2 rounded-md border-2 border-dashed border-primary-light bg-primary-50/30 p-3"
        >
          <div className="flex items-center gap-2">
            <Input value={section.title} onChange={(e) => renameSection(section.id, e.target.value)} className="flex-1" placeholder="Section title" />
            <button type="button" onClick={() => addField(section.id)} className="shrink-0 text-xs text-primary hover:underline">
              + Field
            </button>
            <button type="button" onClick={() => removeSection(section.id)} className="shrink-0 text-xs text-red-500 hover:underline">
              Remove section
            </button>
          </div>
          <p className="text-xs text-gray-500">Staff can save this section on its own and move on before finishing the rest of the step.</p>
          {inSection(section.id).map((f) => (
            <DraggableRow
              key={f.id}
              field={f}
              fields={fields}
              onFieldsChange={onFieldsChange}
              documents={documents}
              onDocumentsChange={onDocumentsChange}
              docTypeOptions={docTypeOptions}
              onDragStart={setDraggingId}
              dragging={draggingId === f.id}
            />
          ))}
          {inSection(section.id).length === 0 && <p className="text-xs text-gray-400">Drag a field here, or add one.</p>}
        </DropZone>
      ))}
    </div>
  );
}
